// The Nuclear Generator's death (§31 S6, design "Nuclear Generator"): wrecked
// by an enemy, it explodes for `POWER.blastDamage` ranged damage on every
// square within `POWER.blastRadius` — pieces of every side (armor first,
// through `damage`), structures (but the ranged-immune walls), belt parts.
// A second Nuclear Generator destroyed by the blast explodes in turn.
//
// **After the move, never inside `makeMove`**: the capturer stands on the
// generator's square when it goes off (design, Example 2), and a search must
// not detonate a real board by thinking about it. Ascending squares, chains
// first-in first-out — one order for every client.

import { fileOf, rankOf } from "./board";
import { invalidateMoves, pieceAt, type Position, setStructureHealth } from "./position";
import { damage } from "./reduce";
import { POWER } from "./rules";
import { wreckBelt } from "./industry/beltParts";
import { hasBelt, type IndustryState, NUCLEAR_GENERATOR, structureAt } from "./industry/state";
import { isRangedImmune, structureHealthOf, wreckStructure } from "./industry/structures";

const within = (a: number, b: number, r: number): boolean =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b))) <= r;

/** Is a Nuclear Generator standing here — the question asked before it is wrecked? */
export const isNuclearAt = (industry: IndustryState, at: number): boolean =>
  structureAt(industry, at)?.kind === NUCLEAR_GENERATOR;

/**
 * Explode the Nuclear Generator that stood on `at`. The caller has already
 * wrecked it. Returns the squares that went off, in order.
 */
export function detonate(position: Position, industry: IndustryState, at: number): number[] {
  const queue = [at];
  const fired: number[] = [];
  const seen = new Set<number>([at]);
  while (queue.length > 0) {
    const centre = queue.shift()!;
    fired.push(centre);
    for (const sq of [...position.cellList]) {
      if (!within(sq, centre, POWER.blastRadius)) continue;
      if (pieceAt(position, sq)) damage(position, industry, sq, POWER.blastDamage);
      const structure = structureAt(industry, sq);
      if (structure && !isRangedImmune(structure)) {
        const left = structureHealthOf(structure) - POWER.blastDamage;
        if (left > 0) {
          structure.health = left;
          setStructureHealth(position, sq, left);
        } else {
          const chain = structure.kind === NUCLEAR_GENERATOR && !seen.has(sq);
          wreckStructure(position, industry, sq);
          if (chain) {
            seen.add(sq);
            queue.push(sq);
          }
        }
      }
      if (hasBelt(industry, sq)) wreckBelt(position, industry, sq);
    }
  }
  invalidateMoves(position);
  return fired;
}
